<script setup lang="ts">
  import { computed } from 'vue';
  import type { IOrder } from '@/interfaces/IOrder';
  import { usePosReservationBanner } from '@/composition/reservations/use-pos-reservation-banner';
  import { usePosMoney } from '@/composition/use-pos-money';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2ReservationStatusBadge from '@/components/v2/reservations/V2ReservationStatusBadge.vue';

  const props = defineProps<{
    tableId: number;
    tableOrders: IOrder[];
    canCreateOrder: boolean;
  }>();
  const emit = defineEmits<{ (event: 'seated', tableId: number): void }>();
  const {
    reservation,
    rows,
    actions,
    seated,
    timeLabel,
    late,
    canSeat,
    hasDepositToApply,
    needsCaja,
    applyDisabled,
    seat,
    applyDeposit,
    view,
  } = usePosReservationBanner(props, (tableId) => emit('seated', tableId));
  const money = usePosMoney();
  const note = computed(
    () => reservation.value?.notesGuest?.slice(0, 160) ?? ''
  );
  const remainder = computed(
    () => reservation.value?.depositUnappliedAmount ?? 0
  );
  const appliedAmount = computed(
    () => reservation.value?.depositAppliedAmount ?? 0
  );
</script>

<template>
  <section
    v-show="reservation"
    class="reservation-table-banner"
    :class="{ seated, 'has-deposit': hasDepositToApply, late: late > 0 }"
    aria-live="polite"
  >
    <template v-if="reservation">
      <div class="banner-main">
        <PosUiIcon
          :name="seated ? 'silverware-fork-knife' : 'calendar-clock'"
          class="banner-icon"
        />
        <div class="banner-copy">
          <p class="banner-title">
            {{
              $t(
                seated
                  ? 'reservations.pos.seatedBanner'
                  : 'reservations.pos.banner',
                {
                  name:
                    reservation.guestName ||
                    $t('reservations.common.anonymous'),
                  time: timeLabel,
                  people: reservation.partySize,
                }
              )
            }}
          </p>
          <p v-if="note && !seated" class="banner-note">{{ note }}</p>
          <p v-if="seated && reservation.depositAppliedAt" class="applied-copy">
            {{
              $t('reservations.pos.seatedBannerDeposit', {
                amount: money.formatComputedMoney(appliedAmount),
              })
            }}
          </p>
        </div>
        <V2ReservationStatusBadge
          v-if="!seated"
          :status="reservation.status"
          :late-minutes="late"
        />
        <span v-if="rows.length > 1" class="more-count">{{
          $t('reservations.pos.more', { count: rows.length - 1 })
        }}</span>
        <div class="banner-actions">
          <PosUiButton
            v-if="canSeat"
            icon="silverware-fork-knife"
            :disabled="actions.busy.value"
            @click="seat"
            >{{ $t('reservations.pos.seat') }}</PosUiButton
          >
          <PosUiButton
            v-if="actions.canManage.value"
            color="neutral"
            variant="ghost"
            :disabled="actions.busy.value"
            @click="view"
            >{{ $t('reservations.pos.view') }}</PosUiButton
          >
        </div>
      </div>
      <div v-if="hasDepositToApply" class="deposit-row">
        <p class="deposit-amount">
          {{
            $t('reservations.pos.depositPaid', {
              amount: money.formatComputedMoney(reservation.depositAmount || 0),
            })
          }}
        </p>
        <PosUiButton
          v-if="actions.canOrders.value"
          color="success"
          variant="soft"
          icon="cash-check"
          :disabled="applyDisabled"
          @click="applyDeposit"
          >{{ $t('reservations.pos.applyDeposit') }}</PosUiButton
        >
        <p
          v-if="actions.canOrders.value && !tableOrders.length"
          class="deposit-hint"
        >
          {{ $t('reservations.pos.applyDepositNoOrders') }}
        </p>
        <p
          v-else-if="actions.canOrders.value && needsCaja"
          class="deposit-hint"
        >
          {{ $t('cajaV2.noCajaTitle') }}
        </p>
      </div>
      <div v-if="remainder > 0" class="deposit-row remainder-row">
        <p class="deposit-amount">
          {{
            $t('reservations.deposit.leftover', {
              amount: money.formatComputedMoney(remainder),
            })
          }}
        </p>
        <template v-if="actions.canManage.value">
          <PosUiButton
            color="neutral"
            variant="outline"
            :disabled="actions.busy.value"
            @click="actions.refundDeposit(remainder)"
            >{{ $t('reservations.deposit.refundLeftover') }}</PosUiButton
          >
          <PosUiButton
            color="neutral"
            variant="ghost"
            :disabled="actions.busy.value"
            @click="actions.keepDeposit()"
            >{{ $t('reservations.deposit.keepLeftover') }}</PosUiButton
          >
        </template>
      </div>
    </template>
  </section>
</template>

<style scoped lang="scss">
  .reservation-table-banner {
    @apply tw-flex-none tw-border-b tw-border-sky-200 tw-bg-sky-50 tw-px-4 tw-py-3;
    &.seated {
      @apply tw-border-neutral-200 tw-bg-canvas;
    }
    &.has-deposit {
      @apply tw-border-emerald-200 tw-bg-emerald-50;
    }
    &.late {
      @apply tw-border-l-4 tw-border-l-red-400;
    }
    .banner-main {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .banner-icon {
      @apply tw-flex-none tw-text-xl tw-text-sky-600;
    }
    &.seated .banner-icon {
      @apply tw-text-emerald-600;
    }
    .banner-copy {
      @apply tw-min-w-0 tw-flex-1;
      min-width: min(180px, 100%);
    }
    .banner-title {
      @apply tw-m-0 tw-break-words tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .banner-note {
      @apply tw-mb-0 tw-mt-1 tw-break-words tw-text-xs tw-text-neutral-600;
    }
    .applied-copy {
      @apply tw-mb-0 tw-mt-1 tw-text-xs tw-font-medium tw-text-emerald-700;
    }
    .more-count {
      @apply tw-whitespace-nowrap tw-text-xs tw-text-neutral-500;
    }
    .banner-actions {
      @apply tw-ml-auto tw-flex tw-flex-none tw-items-center tw-gap-1;
    }
    .deposit-row {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-border-t tw-border-emerald-200/60 tw-pt-3;
    }
    .deposit-amount {
      @apply tw-m-0 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-emerald-700;
    }
    .deposit-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .remainder-row {
      @apply tw-border-neutral-200;
    }
  }
</style>
